import { Component } from '@theme/component';
import { htmlEscape, decodeHtmlEntities } from '@theme/utilities';
import { hydratePDP } from '@theme/hydrate-callers';
import { formatPrice } from '@theme/price';

/**
 * `<pdp-hydration>` — PDP consumer for the APIM `product-detail` endpoint.
 *
 * On connect it reads the NG product ID from its `data-product-id` attribute
 * (rendered by sections/pdp-hydration.liquid from the product's Akeneo product
 * ID metafield) and calls `hydratePDP`. When the response arrives it injects /
 * updates the DOM refs (title, image, price, description) with the NG data.
 *
 * Mirrors the `<header-search>` consumer pattern (assets/header-search.js):
 *   - 1 product  → render the NG fields over the server-rendered fallback
 *   - 0 / null   → keep the server fallback, optionally surface a status note
 *   - error      → surface the error message (with HTTP status when available)
 *
 * The server-rendered children act as a no-JS / pre-hydration fallback so the
 * section is never blank while the request is in flight.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content - Wrapper around the hydrated fields.
 * @property {HTMLElement} [status] - Optional status / error message slot.
 * @property {HTMLElement} [title] - Product caption / title slot.
 * @property {HTMLElement} [image] - Product image slot.
 * @property {HTMLElement} [price] - Product price slot.
 * @property {HTMLElement} [description] - Product description slot.
 * @extends {Component<Refs>}
 */
class PdpHydration extends Component {
  requiredRefs = ['content'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** Fires the NG product-detail fetch for this product's ID. */
  async #load() {
    const productId = (this.dataset.productId || '').trim();

    if (!productId) {
      this.#status('No NG product ID is configured for this product.');
      return;
    }

    this.#status('Loading product details…');

    /** @type {import('@theme/hydrate-callers').HydratePDPResult} */
    let result;
    try {
      result = await hydratePDP({ productId, signal: this.#controller.signal });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#status((error && error.message) || 'Failed to load product details.', true);
      return;
    }

    if (this.#controller.signal.aborted) return;

    const envelope = result.product;

    if (!envelope || !envelope.ok) {
      const status = envelope?.error?.status ? ` (${envelope.error.status})` : '';
      this.#status(`${envelope?.error?.message || 'Failed to load product details.'}${status}`, true);
      return;
    }

    this.#render(envelope.data.product);
  }

  /**
   * Injects the NG product fields into the DOM refs. Falls back gracefully when
   * the response nests the product under a `.product` wrapper (as the
   * product-search hits do) or returns it flat.
   *
   * @param {Object | null} record - The first product record from the NG response.
   */
  #render(record) {
    if (!record) {
      this.#status('No product detail was returned for this product.', true);
      return;
    }

    this.#clearStatus();

    const product = record.product || record;

    const caption = decodeHtmlEntities(product.caption || product.name || '');
    if (caption && this.refs.title) {
      this.refs.title.textContent = caption;
    }

    if (this.refs.image) {
      const imageUrl = product.image?.url || product.image || '';
      this.refs.image.innerHTML =
        typeof imageUrl === 'string' && imageUrl
          ? `<img class="pdp-hydration__img" src="${htmlEscape(imageUrl)}" alt="${htmlEscape(
              caption || 'Product image'
            )}" loading="lazy">`
          : '';
    }

    if (this.refs.price) {
      const price = formatPrice(product.priceInfo);
      this.refs.price.textContent = price;
      this.refs.price.hidden = !price;
    }

    if (this.refs.description) {
      const description = product.description;
      // Description copy may contain merchant HTML — inject as-is when it's a
      // non-empty string, otherwise leave the server fallback untouched.
      if (typeof description === 'string' && description.trim()) {
        this.refs.description.innerHTML = description;
      }
    }

    this.setAttribute('hydrated', '');
  }

  /**
   * Writes a status / error message into the status ref (when present).
   * @param {string} message
   * @param {boolean} [isError]
   */
  #status(message, isError = false) {
    const node = this.refs.status;
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
    node.classList.toggle('pdp-hydration__status--error', Boolean(isError));
    if (isError) node.setAttribute('role', 'alert');
    else node.removeAttribute('role');
  }

  /** Clears any visible status message. */
  #clearStatus() {
    const node = this.refs.status;
    if (!node) return;
    node.textContent = '';
    node.hidden = true;
    node.classList.remove('pdp-hydration__status--error');
    node.removeAttribute('role');
  }
}

if (!customElements.get('pdp-hydration')) {
  customElements.define('pdp-hydration', PdpHydration);
}
